/* eslint-disable max-lines */
import { Button, Input, Pagination, Select, SelectItem, SharedSelection } from '@heroui/react';
import {
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ContentLoadError } from '../ContentLoadError';
import { Flex } from '../Flex';
import { DataTableBody } from './DataTableBody';
import type { DataTableFilterOption, DataTableProps } from './types';
import { DEFAULT_PAGE_SIZE, EMPTY_COLUMN_FILTERS } from './utils';

const globalSearchFn: FilterFn<any> = (row, _columnId, filterValue: string) => {
  const search = filterValue.toLowerCase().trim();
  if (!search) return true;
  return Object.values(row.original as Record<string, unknown>).some((value) => {
    if (typeof value === 'string') return value.toLowerCase().includes(search);
    if (Array.isArray(value)) {
      return value.some((v) => typeof v === 'string' && v.toLowerCase().includes(search));
    }
    return false;
  });
};

export const DataTable = <T extends object>({
  data,
  columns,
  isLoading,
  isError,
  onRefresh,
  pageSize = DEFAULT_PAGE_SIZE,
  filterableColumns,
  searchPlaceholder = 'Search...',
  totalPages,
  currentPage,
  onPageChange,
  filterValues,
  onFilterChange,
  onResetFilters,
  onSearchChange,
  searchValue,
  getRowClassName,
}: DataTableProps<T>): React.JSX.Element => {
  const isServerSidePagination = Boolean(onPageChange);
  const isServerSideFiltering = Boolean(onFilterChange);
  const isServerSideSearch = Boolean(onSearchChange);

  // For server-side search the input is the source of truth locally,
  // and is forwarded upward via a debounced effect so we don't fire a
  // network request on every keystroke.
  const [globalFilter, setGlobalFilter] = useState(searchValue ?? '');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Debounce server-side search forwarding (300ms). Skip if the
  // trimmed value already matches what the parent has — otherwise the
  // mount-time effect would forward an empty string and reset the URL
  // (and pagination) on every page render.
  useEffect(() => {
    if (!isServerSideSearch) return;
    const next = globalFilter.trim();
    const current = (searchValue ?? '').trim();
    if (next === current) return;
    const handle = window.setTimeout(() => {
      onSearchChange!(next);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [globalFilter, isServerSideSearch, onSearchChange, searchValue]);

  // Keep local input in sync if parent resets/controls the search value.
  useEffect(() => {
    if (isServerSideSearch && typeof searchValue === 'string' && searchValue !== globalFilter) {
      setGlobalFilter(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(!isServerSidePagination && { getPaginationRowModel: getPaginationRowModel() }),
    globalFilterFn: globalSearchFn,
    state: {
      // When search is server-side, the parent already filtered the
      // data — we must NOT re-filter on the client (would double-filter
      // and could drop rows whose email casing differs from the input).
      globalFilter: isServerSideSearch ? '' : globalFilter,
      sorting,
      columnFilters: isServerSideFiltering ? EMPTY_COLUMN_FILTERS : columnFilters,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    ...(!isServerSideFiltering && { onColumnFiltersChange: setColumnFilters }),
    ...(!isServerSidePagination && { initialState: { pagination: { pageSize } } }),
  });

  // Skip the very first render for both reset effects below — on
  // mount the filters/search haven't actually "changed", so we must
  // not yank pagination back to page 1 (would defeat e.g. ?page=2 in
  // the URL).
  const didMountRef = useRef(false);

  // Whenever filters or search change in client-side mode, jump back
  // to page 1 — otherwise the pagination would stay on a page index
  // that's now out of range for the filtered result.
  useEffect(() => {
    if (isServerSidePagination) return;
    if (!didMountRef.current) return;
    table.setPageIndex(0);
  }, [globalFilter, columnFilters, isServerSidePagination, table]);

  // For server-side mode the parent owns pagination — when the search
  // term or column filters change, ask the parent to reset to page 1.
  useEffect(() => {
    if (!isServerSidePagination) return;
    if (!onPageChange) return;
    if (!didMountRef.current) return;
    if ((currentPage ?? 1) === 1) return;
    onPageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter, JSON.stringify(filterValues ?? {})]);

  // Mark the component as mounted AFTER the initial effect run.
  useEffect(() => {
    didMountRef.current = true;
  }, []);

  const filterOptions = useMemo<Record<string, DataTableFilterOption[]>>(() => {
    if (!filterableColumns?.length) return {};
    return filterableColumns.reduce<Record<string, DataTableFilterOption[]>>(
      (acc, { key, options }) => {
        if (options) {
          acc[key] = options;
          return acc;
        }
        const values = data
          .flatMap((row) => {
            const v = (row as Record<string, unknown>)[key];
            return Array.isArray(v) ? v : [v];
          })
          .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
        acc[key] = [...new Set(values)].sort().map((v) => ({ value: v, label: v }));
        return acc;
      },
      {},
    );
  }, [data, filterableColumns]);

  const getActiveFilter = (key: string): string => {
    if (isServerSideFiltering) return filterValues?.[key] ?? '';
    return (columnFilters.find((f) => f.id === key)?.value as string) ?? '';
  };

  const handleColumnFilter = (key: string, value: string) => {
    if (isServerSideFiltering) {
      onFilterChange!(key, value);
    } else {
      table.getColumn(key)?.setFilterValue(value || undefined);
      table.setPageIndex(0);
    }
  };

  const handleSearch = (value: string) => {
    setGlobalFilter(value);
    if (!isServerSidePagination) table.setPageIndex(0);
  };

  const hasActiveFilters = globalFilter.length > 0
    || (isServerSideFiltering
      ? Object.values(filterValues ?? {}).some(Boolean)
      : columnFilters.length > 0);

  const resetAll = () => {
    handleSearch('');
    if (isServerSideFiltering) {
      if (onResetFilters) {
        onResetFilters();
      } else {
        filterableColumns?.forEach(({ key }) => onFilterChange!(key, ''));
      }
    } else {
      setColumnFilters([]);
      if (!isServerSidePagination) table.setPageIndex(0);
    }
    if (isServerSideSearch) onSearchChange!('');
  };

  const paginationPage = isServerSidePagination
    ? (currentPage ?? 1)
    : table.getState().pagination.pageIndex + 1;

  const paginationTotal = isServerSidePagination
    ? (totalPages ?? 0)
    : table.getPageCount();

  const handlePageChange = (page: number) => {
    if (isServerSidePagination) {
      onPageChange!(page);
    } else {
      table.setPageIndex(page - 1);
    }
  };

  const rowCount = isLoading
    ? pageSize ?? data.length
    : table.getRowModel().rows.length;

  if (isError) {
    return <ContentLoadError onRefreshClicked={onRefresh} />;
  }

  return (
    <div className="w-full space-y-3">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 px-4 pt-4 pb-3 flex-wrap">
          <Input
            size="sm"
            placeholder={searchPlaceholder}
            value={globalFilter}
            onValueChange={handleSearch}
            startContent={<Search size={14} className="text-gray-400" />}
            classNames={{
              base: 'max-w-[260px] min-w-[200px]',
              inputWrapper: 'border border-gray-200 shadow-none bg-gray-50 h-10 rounded-full',
            }}
            isClearable
            onClear={() => handleSearch('')}
            aria-label={searchPlaceholder}
          />

          {filterableColumns && filterableColumns.length > 0 && (
            <div className="hidden h-6 mx-2 border-l border-gray-200 sm:block" />
          )}

          <div className="flex flex-wrap items-center gap-2 flex-1">
            {filterableColumns?.map(({ key, label }) => {
              const active = getActiveFilter(key);
              const opts = filterOptions[key] ?? [];
              return (
                <div key={key} className="flex items-center gap-1">
                  <Select
                    size="sm"
                    label={label}
                    aria-label={label}
                    className="min-w-[140px]"
                    selectedKeys={active ? [active] : []}
                    onSelectionChange={(keys: SharedSelection) => {
                      const value = (Array.from(keys as Set<string>)[0] as string) ?? '';
                      handleColumnFilter(key, value);
                    }}
                    classNames={{
                      trigger: `h-10 rounded-full text-sm shadow-none ${
                        active ? 'border-primary bg-gray-50' : 'border border-gray-200 bg-gray-50'
                      }`,
                    }}
                  >
                    {opts.map((opt) => (
                      <SelectItem key={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </Select>
                  {active && (
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      className="min-w-unit-6 h-10 text-gray-400 hover:text-gray-700"
                      onPress={() => handleColumnFilter(key, '')}
                      aria-label={`Clear ${label} filter`}
                    >
                      <X size={12} />
                    </Button>
                  )}
                </div>
              );
            })}

            {hasActiveFilters && (
              <Button
                size="sm"
                variant="light"
                className="ml-auto text-xs font-medium text-gray-600 hover:text-gray-900"
                onPress={resetAll}
              >
                Reset all
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table
            className="min-w-full border-t border-gray-100"
            role="grid"
            aria-rowcount={rowCount}
            aria-colcount={columns.length}
            aria-busy={Boolean(isLoading)}
          >
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-gray-200">
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();

                    let ariaSort: 'ascending' | 'descending' | 'none' | undefined;
                    if (!canSort) {
                      ariaSort = undefined;
                    } else if (sorted === 'asc') {
                      ariaSort = 'ascending';
                    } else if (sorted === 'desc') {
                      ariaSort = 'descending';
                    } else {
                      ariaSort = 'none';
                    }

                    if (!header.isPlaceholder) {
                      const meta = header.column.columnDef.meta as
                        | { align?: 'left' | 'center' | 'right'; width?: string }
                        | undefined;
                      const align = meta?.align ?? 'left';
                      const alignClass = align === 'center'
                        ? 'text-center'
                        : align === 'right'
                          ? 'text-right'
                          : 'text-left';
                      const innerJustify = align === 'center'
                        ? 'justify-center'
                        : align === 'right'
                          ? 'justify-end'
                          : '';
                      return (
                        <th
                          key={header.id}
                          scope="col"
                          className={`px-4 py-3 whitespace-nowrap ${alignClass} ${meta?.width ?? ''} text-xs font-semibold text-gray-500 uppercase tracking-wider`}
                          aria-sort={ariaSort}
                        >
                          {canSort ? (
                            <button
                              type="button"
                              onClick={header.column.getToggleSortingHandler()}
                              className={`inline-flex items-center gap-1.5 select-none hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded-full px-1 -mx-1 ${innerJustify}`}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <span className={sorted ? 'text-primary' : 'text-gray-300'}>
                                {sorted === 'asc' && <ArrowUp size={13} strokeWidth={2.5} />}
                                {sorted === 'desc' && <ArrowDown size={13} strokeWidth={2.5} />}
                                {!sorted && <ChevronsUpDown size={13} strokeWidth={2} />}
                              </span>
                            </button>
                          ) : (
                            <span className={`inline-flex items-center gap-1.5 ${innerJustify}`}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                          )}
                        </th>
                      );
                    }

                    return <th key={header.id} />;
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              <DataTableBody
                table={table}
                columns={columns}
                isLoading={isLoading}
                pageSize={pageSize}
                getRowClassName={getRowClassName}
              />
            </tbody>
          </table>
        </div>
      </div>

      {paginationTotal > 1 && !isLoading && (
        <Flex justifyContent="flex-end" margin="4px 0 0">
          <Pagination
            className="z-0"
            page={paginationPage}
            showControls
            color="primary"
            variant="light"
            total={paginationTotal}
            size="sm"
            onChange={handlePageChange}
          />
        </Flex>
      )}
    </div>
  );
};
